/**
 * Relative Time Utilities
 *
 * Calculates and formats relative time strings using date-fns with locale support.
 * Delegates to date-fns formatDistanceToNow for proper i18n of relative times
 * like "hace 3 días", "fa 3 dies", "3 days ago".
 */

import { formatDistanceToNow } from 'date-fns';
import { getDateFnsLocale } from '../i18n/dateFnsLocale';

export type Locale = 'es' | 'en' | 'ca';

interface RelativeTimeOptions {
  locale?: Locale;
  showSeconds?: boolean;
}

/**
 * Calculates the difference between a date and now, returning a localized
 * relative time string using date-fns formatDistanceToNow.
 *
 * Examples (Spanish): "hace 3 días", "hace 1 minuto", "ahora mismo"
 * Examples (Catalan): "fa 3 dies", "fa 1 minut", "ara mateix"
 * Examples (English): "3 days ago", "1 minute ago", "just now"
 *
 * @param dateInput - The date to compare (string, Date, or timestamp)
 * @param options - Configuration options (locale, showSeconds)
 * @returns Localized human-readable relative time string
 */
export function getRelativeTime(
  dateInput: string | Date | number,
  options: RelativeTimeOptions = {}
): string {
  const { locale = 'es', showSeconds = false } = options;

  // Parse the input date
  const date = typeof dateInput === 'string'
    ? new Date(dateInput)
    : typeof dateInput === 'number'
      ? new Date(dateInput)
      : dateInput;

  // Validate the date
  if (isNaN(date.getTime())) {
    return locale === 'es' ? 'fecha inválida' : locale === 'ca' ? 'data no vàlida' : 'invalid date';
  }

  const dateFnsLocale = getDateFnsLocale(locale);

  // For very recent timestamps (< 1 minute), show seconds if enabled
  const now = new Date();
  const diffMs = Math.abs(now.getTime() - date.getTime());
  const diffSeconds = Math.floor(diffMs / 1000);

  if (diffSeconds < 10) {
    // Less than 10 seconds ago
    if (locale === 'es') return 'ahora';
    if (locale === 'ca') return 'ara';
    return 'just now';
  }

  if (showSeconds && diffSeconds < 60) {
    // Between 10 and 60 seconds - show seconds
    return formatDistanceToNow(date, {
      locale: dateFnsLocale,
      addSuffix: true,
      includeSeconds: true,
    });
  }

  return formatDistanceToNow(date, {
    locale: dateFnsLocale,
    addSuffix: true,
  });
}

/**
 * Gets a short relative time string suitable for compact displays.
 * Uses abbreviated format without "ago"/"hace" prefix.
 */
export function getShortRelativeTime(
  dateInput: string | Date | number,
  locale: Locale = 'es'
): string {
  const date = typeof dateInput === 'string'
    ? new Date(dateInput)
    : typeof dateInput === 'number'
      ? new Date(dateInput)
      : dateInput;

  if (isNaN(date.getTime())) {
    return '?';
  }

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  if (diffMs < 0) {
    if (locale === 'es') return 'futuro';
    if (locale === 'ca') return 'futur';
    return 'future';
  }

  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 1) {
    if (locale === 'es') return 'ahora';
    if (locale === 'ca') return 'ara';
    return 'now';
  }
  if (diffHours < 1) return `${diffMinutes}m`;
  if (diffDays < 1) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo`;
  return `${Math.floor(diffDays / 365)}y`;
}

/**
 * Determines if the relative time should auto-update.
 * Returns the recommended update interval in milliseconds.
 */
export function getRelativeTimeUpdateInterval(dateInput: string | Date | number): number {
  const date = typeof dateInput === 'string'
    ? new Date(dateInput)
    : typeof dateInput === 'number'
      ? new Date(dateInput)
      : dateInput;

  if (isNaN(date.getTime())) return 0;

  const now = new Date();
  const diffMs = Math.abs(now.getTime() - date.getTime());
  const diffMinutes = Math.floor(diffMs / 60000);

  // Update every second if less than 1 minute
  if (diffMinutes < 1) return 1000;
  // Update every minute if less than 1 hour
  if (diffMinutes < 60) return 60000;
  // Update every hour if less than 1 day
  if (diffMinutes < 1440) return 3600000;
  // No need to update for older dates
  return 0;
}

export type { RelativeTimeOptions };
